import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import { toast } from 'sonner';

interface FeatureFlags {
  showOpportunityAssessment: boolean;
  showReadinessAssessment: boolean;
}

interface FeatureConfig {
  modules: {
    public?: string[];
    beta?: string[];
    internal?: string[];
  };
}

interface FeatureFlagsContextType {
  featureFlags: FeatureFlags;
  isLoading: boolean;
  error: string | null;
  refreshFlags: () => Promise<void>;
}

// Provide default values (all features disabled)
const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  featureFlags: {
    showOpportunityAssessment: false,
    showReadinessAssessment: false,
  },
  isLoading: false,
  error: null,
  refreshFlags: async () => {},
});

export const FeatureFlagsProvider: React.FC<{ 
  children: React.ReactNode;
  session?: Session | null;
}> = ({ children, session }) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({
    showOpportunityAssessment: false,
    showReadinessAssessment: false,
  });
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [error, setError] = useState<string | null>(null);
  
  // Track the last session to prevent unnecessary refreshes
  const lastSessionRef = useRef<Session | null>(null);

  const fetchFeatureConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[FeatureFlags] Starting to fetch feature config...');
      

      // Check if Supabase is properly configured
      if (!supabase) {
        console.error('Supabase client is not properly configured');
        const defaultFlags = {
          showOpportunityAssessment: false,
          showReadinessAssessment: false,
        };
        setFeatureFlags(defaultFlags);
        toast.error('Configuration error', { id: 'feature-flags' });
        return;
      }

      // Check if the function exists before calling it
      const { data, error } = await supabase.rpc('get_feature_config');
      
      console.log('[FeatureFlags] RPC response:', { data, error });
      
      if (error) {
        console.error('Error fetching feature config:', error);
        // Set default flags for non-logged-in users or when function doesn't exist
        const defaultFlags = {
          showOpportunityAssessment: false,
          showReadinessAssessment: false,
        };
        console.log('[FeatureFlags] Using default flags due to error:', defaultFlags);
        setFeatureFlags(defaultFlags);
        setError(error.message);
        toast.error('Failed to load features', { id: 'feature-flags' });
        return;
      }
      
      console.log('Feature config modules:', data);
      
      // Update feature flags based on RPC response
      if (data && data.modules) {
        const config = data as FeatureConfig;
        const publicModules = config.modules.public || [];
        const betaModules = config.modules.beta || [];
        const internalModules = config.modules.internal || [];
        
        console.log('[FeatureFlags] Modules from config:', {
          public: publicModules,
          beta: betaModules,
          internal: internalModules
        });
        

        // Helper function to check if a module exists in any of the arrays
        const hasModule = (moduleKey: string) => {
          return publicModules.includes(moduleKey) || 
                 betaModules.includes(moduleKey) || 
                 internalModules.includes(moduleKey);
        };
        
        const newFlags = {
          showOpportunityAssessment: true, // Temporarily enable for testing
          showReadinessAssessment: hasModule('readiness_assessment'),
        };
        
        console.log('[FeatureFlags] Setting feature flags:', newFlags);
        console.log('[FeatureFlags] Public modules from Supabase:', publicModules);
        console.log('[FeatureFlags] Beta modules from Supabase:', betaModules);
        console.log('[FeatureFlags] Internal modules from Supabase:', internalModules);
        
        setFeatureFlags(newFlags);
      } else {
        // Only set default flags if we got a response but it had no modules
        // This means the RPC call succeeded but returned no data
        console.log('[FeatureFlags] RPC succeeded but no modules found, using default flags');
        const defaultFlags = {
          showOpportunityAssessment: true, // Temporarily enable for testing
          showReadinessAssessment: false,
        };
        setFeatureFlags(defaultFlags);

      }
    } catch (err) {
      console.error('Exception fetching feature config:', err);
      // Set default flags for non-logged-in users or when function doesn't exist
      const defaultFlags = {
        showOpportunityAssessment: false,
        showReadinessAssessment: false,
      };
      console.log('[FeatureFlags] Using default flags due to exception:', defaultFlags);
      setFeatureFlags(defaultFlags);
      setError(err instanceof Error ? err.message : 'Unknown error');
      toast.error('Failed to load features', { id: 'feature-flags' });
    } finally {
      setIsLoading(false);
      console.log('[FeatureFlags] Finished loading feature flags');
    }
  }, []); // Add empty dependency array to prevent infinite re-renders

  const refreshFlags = useCallback(async () => {
    await fetchFeatureConfig();
  }, [fetchFeatureConfig]);

  // Initial fetch on mount
  useEffect(() => {
    fetchFeatureConfig();
  }, [fetchFeatureConfig]);

  useEffect(() => {
    // Only refresh if the session has actually changed
    const currentSessionId = session?.user?.id || null;
    const lastSessionId = lastSessionRef.current?.user?.id || null;
    
    if (currentSessionId !== lastSessionId) {
      console.log('[FeatureFlags] Session changed, refreshing flags:', { 
        from: lastSessionId, 
        to: currentSessionId 
      });
      lastSessionRef.current = session;
      fetchFeatureConfig();
    }
  }, [fetchFeatureConfig, session]);

  const value: FeatureFlagsContextType = {
    featureFlags,
    isLoading,
    error,
    refreshFlags,
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context.featureFlags;
};

export const useFeatureFlagsContext = () => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlagsContext must be used within a FeatureFlagsProvider');
  }
  return context;
};
